// src/pages/RoleManagement/index.tsx
import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell from 'Components/Common/DataTableShell';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import CreateRoleModal from './modals/CreateRoleModal';
import EditRoleModal from './modals/EditRoleModal';
import PermissionAssignModal from './modals/PermissionAssignModal';
import ApiPermissionAssignModal from './modals/ApiPermissionAssignModal';
import DeleteRoleDialog from './modals/DeleteRoleDialog';
import RoleModulesListModal from './modals/RoleModulesListModal';
import {
  setSelectedRoleGroup,
  openModal,
  closeModal,
} from 'slices/roleManagement/reducer';
import {
  fetchRoleGroups,
  fetchRoleGroupDetail,
  fetchModules,
  fetchPermissionsByModule,
  deleteRoleGroupById,
} from 'slices/roleManagement/thunk';
import { Module, RoleGroup } from './types';
import { useBrandingTitle } from '../../hooks';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

const RoleManagement: React.FC = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const {
    roleGroups,
    modules,
    selectedRoleGroup,
    loading,
  } = useSelector((state: any) => state.RoleManagement);

  const modalState = useSelector((state: any) => state.RoleManagement.modalState);

  // Role đang được xem popup danh sách đầy đủ Module — bấm vào badge "+N" hoặc chữ "Chưa gán module"
  // sẽ mở popup này (mục 9, REQUIRE_ROLEMODULE.md)
  const [modulesListRole, setModulesListRole] = useState<RoleGroup | null>(null);

  useBrandingTitle(st('quan_tri_nhom_quyen_text'));

  const loadData = () => {
    dispatch(fetchRoleGroups({ pageIndex: 0, pageSize: 50 }));
    dispatch(fetchModules());
    dispatch(fetchPermissionsByModule());
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lỗi đã được toast trực tiếp trong từng handler (CreateRoleModal/EditRoleModal tự xử lý,
  // handleEditRole/handleOpenPermissionAssign/.../handleConfirmDelete ở dưới) ngay tại nơi catch —
  // KHÔNG toast lại ở đây theo Redux state.error, vì các *.rejected reducer cũng set state.error
  // cho message tương tự, dẫn tới hiển thị 2 toast trùng nội dung cho cùng 1 lỗi (cùng bug đã sửa ở
  // UserManagement/index.tsx).

  const handleCreateRole = () => {
    dispatch(openModal('create'));
  };

  const handleEditRole = async (roleGroup: RoleGroup) => {
    try {
      await dispatch(fetchRoleGroupDetail(roleGroup.id)).unwrap();
      dispatch(openModal('edit'));
    } catch (error: any) {
      toast.error(error || st('co_loi_xay_ra_khi_tai_message'));
    }
  };

  const handleOpenPermissionAssign = async (roleGroup: RoleGroup) => {
    try {
      await dispatch(fetchRoleGroupDetail(roleGroup.id)).unwrap();
      dispatch(openModal('permissionAssign'));
    } catch (error: any) {
      toast.error(error || st('co_loi_xay_ra_khi_tai_message'));
    }
  };

  const handleOpenApiPermissionAssign = async (roleGroup: RoleGroup) => {
    try {
      await dispatch(fetchRoleGroupDetail(roleGroup.id)).unwrap();
      dispatch(openModal('apiPermissionAssign'));
    } catch (error: any) {
      toast.error(error || st('co_loi_xay_ra_khi_tai_message'));
    }
  };

  const handleDeleteRole = (roleGroup: RoleGroup) => {
    dispatch(setSelectedRoleGroup(roleGroup));
    dispatch(openModal('delete'));
  };

  const handleConfirmDelete = async () => {
    if (!selectedRoleGroup) return;

    try {
      await dispatch(deleteRoleGroupById(selectedRoleGroup.id)).unwrap();
      toast.success(st('xoa_nhom_quyen_thanh_cong_message'));
      dispatch(closeModal('delete'));
      dispatch(setSelectedRoleGroup(null));
      loadData();
    } catch (error: any) {
      toast.error(error || st('co_loi_xay_ra_khi_xoa_message'));
    }
  };

  const getAssignedModules = (roleGroup: RoleGroup): Module[] => {
    const selectedIds = new Set(roleGroup.moduleIds || []);
    return modules.filter((module: Module) => selectedIds.has(module.id));
  };

  const getPermissionTotal = (assignedModules: Module[]) =>
    assignedModules.reduce((total, module) => total + (module.permissionCount || 0), 0);

  // Tập action-code tĩnh của cột "Thao tác" ở bảng nhóm quyền — dùng để ẩn hẳn cột này khi role
  // hiện tại không có quyền với bất kỳ action nào trong cột.
  const ROLE_ACTION_COLUMN_CODES = ['button-edit', 'button-permission-assign', 'button-api-permission', 'button-delete'];
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    ROLE_ACTION_COLUMN_CODES,
  );

  const renderTableBody = () => {
    if (loading && roleGroups.length === 0) {
      return Array.from({ length: 5 }).map((_, row) => (
        <tr key={row}>
          {Array.from({ length: canShowActionColumn ? 8 : 7 }).map((__, col) => (
            <td key={col}>
              <div className="placeholder-glow">
                <span className="placeholder col-8"></span>
              </div>
            </td>
          ))}
        </tr>
      ));
    }

    if (roleGroups.length === 0) {
      return (
        <tr>
          <td colSpan={canShowActionColumn ? 8 : 7} className="text-center text-muted py-5">
            <div className="mb-2">
              <i className="ri-shield-user-line fs-1 text-muted"></i>
            </div>
            {st('chua_co_nhom_quyen_nao_text')}
          </td>
        </tr>
      );
    }

    return roleGroups.map((roleGroup: RoleGroup, rowIndex: number) => {
      const assignedModules = getAssignedModules(roleGroup);
      const visibleModules = assignedModules.slice(0, 4);
      const hiddenModuleCount = Math.max(assignedModules.length - visibleModules.length, 0);

      return (
        <tr key={roleGroup.id}>
          <td className="text-center">{rowIndex + 1}</td>
          <td className="fw-medium">{roleGroup.code}</td>
          <td>
            <div className="fw-semibold">{roleGroup.name}</div>
            {roleGroup.description && (
              <small className="text-muted d-block text-truncate" style={{ maxWidth: 320 }}>
                {roleGroup.description}
              </small>
            )}
          </td>
          <td>
            {roleGroup.isProtected ? (
              // Role IsProtected = true bypass toàn bộ RoleModuleMapping (xem GetUserMenuQueryHandler)
              // nên luôn có full quyền truy cập menu dù không có (và không cần) dòng RoleModuleMapping nào.
              <Badge color="success" className="bg-success-subtle text-success">
                <i className="ri-verified-badge-line me-1"></i>
                {st('toan_quyen_truy_cap_text')}
              </Badge>
            ) : assignedModules.length === 0 ? (
              <span className="text-muted">{st('chua_gan_module_text')}</span>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                {visibleModules.map(module => (
                  <Badge key={module.id} color="primary" className="bg-primary-subtle text-primary">
                    {module.name}
                  </Badge>
                ))}
                {hiddenModuleCount > 0 && (
                  <Badge
                    color="secondary"
                    className="bg-secondary-subtle text-secondary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setModulesListRole(roleGroup)}
                  >
                    +{hiddenModuleCount}
                  </Badge>
                )}
              </div>
            )}
          </td>
          <td>{getPermissionTotal(assignedModules)}</td>
          <td>{roleGroup.userCount || 0}</td>
          <td>
            <div className="d-flex flex-wrap gap-1">
              <Badge color={roleGroup.isActive ? 'success' : 'danger'} className={`bg-${roleGroup.isActive ? 'success' : 'danger'}-subtle text-${roleGroup.isActive ? 'success' : 'danger'}`}>
                {roleGroup.isActive ? st('dang_bat_text') : st('da_tat_text')}
              </Badge>
              {roleGroup.isDefault && (
                <Badge color="info" className="bg-info-subtle text-info">
                  {st('mac_dinh_text')}
                </Badge>
              )}
              {roleGroup.isProtected && (
                <Badge color="warning" className="bg-warning-subtle text-warning">
                  {st('he_thong_text')}
                </Badge>
              )}
            </div>
          </td>
          {canShowActionColumn && (
            <td className="text-end">
              <div className="d-flex justify-content-end gap-1">
                {PageActionPermission.canDisplay(location.pathname, "button-edit") && (
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    className="btn-icon"
                    title={st('sua_text')}
                    aria-label={st('sua_nhom_quyen_label')}
                    data-action-code="button-edit"
                    data-action-name="Sửa nhóm quyền"
                    onClick={() => handleEditRole(roleGroup)}
                  >
                    <i className="ri-pencil-line"></i>
                  </Button>
                )}
                {PageActionPermission.canDisplay(location.pathname, "button-permission-assign") && (
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    className="btn-icon"
                    title={st('cap_nhat_permission_title')}
                    aria-label={st('cap_nhat_permission_title')}
                    data-action-code="button-permission-assign"
                    data-action-name="Cập nhật permission"
                    onClick={() => handleOpenPermissionAssign(roleGroup)}
                  >
                    <i className="ri-shield-keyhole-line"></i>
                  </Button>
                )}
                {PageActionPermission.canDisplay(location.pathname, "button-api-permission") && (
                  <Button
                    color="info"
                    outline
                    size="sm"
                    className="btn-icon"
                    title={st('phan_quyen_api_title')}
                    aria-label={st('phan_quyen_api_title')}
                    data-action-code="button-api-permission"
                    data-action-name="Phân quyền API"
                    onClick={() => handleOpenApiPermissionAssign(roleGroup)}
                  >
                    <i className="ri-plug-2-line"></i>
                  </Button>
                )}
                {!roleGroup.isProtected && !roleGroup.isDefault && PageActionPermission.canDisplay(location.pathname, "button-delete") && (
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    className="btn-icon"
                    title={st('xoa_text')}
                    aria-label={st('xoa_nhom_quyen_label')}
                    data-action-code="button-delete"
                    data-action-name="Xóa nhóm quyền"
                    onClick={() => handleDeleteRole(roleGroup)}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                )}
              </div>
            </td>
          )}
        </tr>
      );
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('quan_tri_nhom_quyen_he_thong_title')} pageTitle="Trang" />

          <Card className="mb-3">
            <CardHeader>
              <Row className="align-items-center gy-3">
                <Col>
                  <h5 className="card-title mb-0">{st('quan_tri_nhom_quyen_text')}</h5>
                  <p className="text-muted mb-0">
                    {st('quan_ly_cac_nhom_quyen_va_text')}
                  </p>
                </Col>
                <Col md="auto">
                  {PageActionPermission.canDisplay(location.pathname, "button-add") && (
                    <Button
                      color="primary"
                      data-action-code="button-add"
                      data-action-name="Thêm nhóm quyền mới"
                      onClick={handleCreateRole}
                    >
                      <i className="ri-add-line me-1"></i>
                      {st('them_nhom_quyen_moi_button')}
                    </Button>
                  )}
                </Col>
              </Row>
            </CardHeader>
          </Card>

          <Card>
            <CardBody className="pt-0">
              <DataTableShell
                headerRow={
                  <tr>
                    <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
                    <th>{st('ma_nhom_column')}</th>
                    <th>{st('ten_nhom_quyen_column')}</th>
                    <th>{st('module_duoc_gan_column')}</th>
                    <th>{st('quyen_hint_2')}</th>
                    <th>{st('thanh_vien_hint')}</th>
                    <th>{st('trang_thai_column')}</th>
                    {canShowActionColumn && (
                      <th className="text-end" style={{ width: 120 }}>{st('thao_tac_text')}</th>
                    )}
                  </tr>
                }
              >
                {renderTableBody()}
              </DataTableShell>
            </CardBody>
          </Card>
        </Container>
      </div>

      <CreateRoleModal
        isOpen={modalState.create}
        toggle={() => dispatch(closeModal('create'))}
        modules={modules}
        onSuccess={loadData}
      />

      <EditRoleModal
        isOpen={modalState.edit}
        toggle={() => dispatch(closeModal('edit'))}
        roleGroup={selectedRoleGroup}
        onSuccess={loadData}
      />

      <PermissionAssignModal
        isOpen={modalState.permissionAssign}
        toggle={() => dispatch(closeModal('permissionAssign'))}
        roleGroup={selectedRoleGroup}
        modules={modules}
        onSuccess={loadData}
      />

      <ApiPermissionAssignModal
        isOpen={modalState.apiPermissionAssign}
        toggle={() => dispatch(closeModal('apiPermissionAssign'))}
        roleGroup={selectedRoleGroup}
        onSuccess={loadData}
      />

      <DeleteRoleDialog
        isOpen={modalState.delete}
        onClose={() => dispatch(closeModal('delete'))}
        onDelete={handleConfirmDelete}
        roleGroup={selectedRoleGroup}
      />

      <RoleModulesListModal
        isOpen={!!modulesListRole}
        toggle={() => setModulesListRole(null)}
        roleGroup={modulesListRole}
        assignedModules={modulesListRole ? getAssignedModules(modulesListRole) : []}
      />
    </React.Fragment>
  );
};

export default RoleManagement;
